import {CoordinateParserService,useCoordinateParserService, ECoordinateType} from '@/app/shared/service/coordinate-parser/coordinate-parser-service';
import {isEmpty, map} from "lodash-es";
import IpproAutocomplete from '@/app/shared/components/ippro-autocomplete';
import {computed, defineComponent, ref, useTemplateRef, watch} from 'vue';
import {AutoCompleteResult} from '@/app/shared/components/ippro-autocomplete/ippro-autocomplete';
import {useKlMapSearchStore} from '@/app/shared/components/kl-map-search/kl-map-search-store';

export enum ESearchType {
  Address = 'address',
  Coordinate = 'coordinate',
  Parcel = 'parcel',
}

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlMapSearch',
    components: {
        IpproAutocomplete
    },
    emits: ['@update:search', 'change', 'extent-selected', 'coordinate-selected'],
    props: {
        modelValue: {
            type: String,
            default: ``,
        },
        minLength: {
            type: Number,
            default: 3,
        },
        projection: {
            type: String,
            default: 'EPSG:31370',
        }
    },
    setup(props, { emit }) {

        const autocompleteRef = useTemplateRef<InstanceType<typeof IpproAutocomplete>>('autocompleteRef'); // = ippro-autocomplete

        const data = ref<AutoCompleteResult[]>([]);
        const searchField = ref('');

        const _coordinateParser: CoordinateParserService = useCoordinateParserService();
        const _mapSearchStore = useKlMapSearchStore();

        const fetching = computed((): boolean => _mapSearchStore.fetching);

        let _coordinateData: AutoCompleteResult;
        let _addressData: AutoCompleteResult[];
        let _parcelData: AutoCompleteResult[];

        const _mergeData = (): AutoCompleteResult[] => {
            let result = [];
            if (_coordinateData) {
                result.push(_coordinateData);
            }
            if (!isEmpty(_addressData)) {
                result = result.concat(_addressData);
            }
            if (!isEmpty(_parcelData)) {
                result = result.concat(_parcelData);
            }
            return result;
        }

        const onInput = (newValue: string) => {
            searchField.value = newValue;

            _handleInput().then();
            emit("@update:search", searchField.value);
        }

        const _handleInput = async () => {
            if (isEmpty(searchField.value)) {
                data.value = [];
                return;
            }
            if (searchField.value.length <= props.minLength) {
                return;
            }

            _coordinateData = _parseCoordinate(searchField.value);

            // opt: only continue search when input cannot be parsed to a coordinate
            if (isEmpty(_coordinateData)) {
                _addressData = map(await _mapSearchStore.fetchAddressSuggestions(searchField.value), (item: string) => ({
                    title: item,
                    value: {
                        type: ESearchType.Address,
                        name: item
                    }
                }));
                _parcelData = map(await _mapSearchStore.fetchParcelSuggestions(searchField.value), (item: string) => ({
                    title: `Perceel: ${item}`,
                    value: {
                        type: ESearchType.Parcel,
                        name: item
                    }
                }));
            } else {
                _addressData = null;
                _parcelData = null;
            }

            data.value = _mergeData();
        }

        const onSelect = (item: { name: string, type: ESearchType }) => {
            if (!item?.name) {
                return;
            }

            emit("change", item.name);
            searchField.value = item.name;

            if (item.type === ESearchType.Address) {
                _onSelectAddress(item.name);
            }
            if (item.type === ESearchType.Coordinate) {
                _onSelectCoordinate(item.name);
            }
            if (item.type === ESearchType.Parcel) {
                _onSelectParcel(item.name);
            }
        }

        const focusSearch = () => {

            autocompleteRef.value.focus();

            // TODO: investigate
            // without the setTimeout: for some strange reason, the dropdown will not be shown here for coordinates
            // (coordinates is the only search without backend call)
            setTimeout(() => {
                _handleInput().then(); // refresh search on previous input
            }, 0);
        }

        const _parseCoordinate = (input: string): AutoCompleteResult => {
            const parsedCoordinate = _coordinateParser.parseCoordinate(input);
            if (!parsedCoordinate) {
                return null;
            }

            const formattedCoordinate = _coordinateParser.formatCoordinate(parsedCoordinate);
            const coordinateTypeLabelMap = {};
            coordinateTypeLabelMap[ECoordinateType.lambert72] = 'Lambert 72 coördinaat';
            coordinateTypeLabelMap[ECoordinateType.lambert2008] = 'Lambert 2008 coördinaat';
            coordinateTypeLabelMap[ECoordinateType.wgs84] = 'WGS84 coördinaat';

            const label = coordinateTypeLabelMap[parsedCoordinate.spatialReference.wkid];

            return {
                title: label ? `<span>${label}:</span><br/><span class="vl-u-text--bold">${formattedCoordinate}</span>` : formattedCoordinate,
                value: {
                    type: ESearchType.Coordinate,
                    name: formattedCoordinate
                }
            };
        }

        const _onSelectAddress = async (address: string) => {
            const extent = await _mapSearchStore.fetchAddressExtent(address, props.projection);
            if (extent) {
                emit('extent-selected', extent);
            }
        }

        const _onSelectParcel = async (capakey: string) => {
            const extent = await _mapSearchStore.fetchParcelExtent(capakey, props.projection);
            if (extent) {
                emit('extent-selected', extent);
            }
        }

        const _onSelectCoordinate = (coordinateString: string) => {
            const parsed = _coordinateParser.parseCoordinate(coordinateString);
            const lambert = _coordinateParser.convertCoordinateToLambertEPSG(parsed, props.projection);
            emit('coordinate-selected', lambert);
        }


        watch(() => props.modelValue, (newValue) => { searchField.value = newValue; });

        return {
            autocompleteRef,

            data,
            fetching,
            searchField,

            onInput,
            onSelect,
            focusSearch,
        }
    }
})
